'use strict';

var lib_currencyCodes = require('./currency-codes.js');

var AMOUNT_PART_SIZE = 3;
var CARD_NUMBER_PART_SIZE = 4;
var ACCOUNT_SPLIT_REGEX = /^(\d{5})(\d{3})(\d)(\d{4})(\d{7})$/;
var ACCOUNT_FORMAT = '$1 $2 $3 $4 $5';
var AMOUNT_DEFORMAT_REGEXP = / /g;
var NBSP = '\u00A0';
var NBH = '\u2011';
var MDASH = '\u2014';
var TRAILING_ZEROS_REGEXP = /\.0+$/;
var RUR_CODE = 'RUR';
/**
 * Возвращает функцию, которая разбивает строку на части.
 *
 * @function
 * @param {Number} partSize Число символов в каждой части
 * @returns {Function}
 */
function createSplitter(partSize) {
    function parts(str) {
        if (str.length <= partSize) {
            return [str];
        }
        var from = str.length - partSize;
        var to = str.length;
        return [str.slice(from, to)].concat(parts(str.slice(0, from)));
    }
    return parts;
}
var amountSplitter = createSplitter(AMOUNT_PART_SIZE);
var cardNumberSplitter = createSplitter(CARD_NUMBER_PART_SIZE);
/**
 * Возвращает символ валюты.
 *
 * @function
 * @param {String} value Мнемоник код валюты
 * @returns {String}
 */
function formatCurrency(value) {
    // TODO typescript error
    // eslint-disable-next-line
    // @ts-ignore
    return lib_currencyCodes[value] || value;
}
/**
 * Возвращает отформатированное значение числа.
 * Целая часть числа разделяется на части по 3 цифры, между целой и дробной части добавляется пробел.
 *
 * @function
 * @param {String|Number} value_ Число
 * @returns {String}
 */
function formatAmount(value_) {
    var value = "".concat(value_);
    var numberParts = value.split('.');
    var integerPart = numberParts[0];
    var decimalPart = numberParts[1] || '';
    if (decimalPart.length === 1) {
        decimalPart += '0';
    }
    return amountSplitter(integerPart).reverse().join(' ') + (decimalPart ? ",".concat(decimalPart) : '');
}
/**
 * Возвращает значение без пробелов.
 *
 * @function
 * @param {String|Number} value Число
 * @returns {String}
 */
function deformatAmount(value) {
    return ("".concat(value)).replace(AMOUNT_DEFORMAT_REGEXP, '');
}
/**
 * Возвращает отформатированное значение аккаунта.
 * Разделяет число на группы, разделяет их пробелами.
 * XXXXX XXX X XXXX XXXXXXX
 *
 * @function
 * @param {String|Number} value Номер аккаунта.
 * @returns {String}
 */
function formatAccount(value) {
    if (!value) {
        return '';
    }
    return value.toString().replace(ACCOUNT_SPLIT_REGEX, ACCOUNT_FORMAT);
}
/**
 * Возвращает отформатированное значение аккаунта.
 * Разделяет число на группы, часть символов заменяет точками.
 * XXXXX XXX...XXXXXX.
 *
 * @function
 * @param {String} account Номер аккаунта.
 * @returns {String}
 */
function formatAccountWithShrinking(account) {
    if (!account) {
        return '';
    }
    return "".concat(account.substr(0, 5), " ").concat(account.substr(5, 3), "...").concat(account.substr(-6));
}
/**
 * Возвращает отформатированное значение номера карты.
 *
 * @function
 * @param {String|Number} value_ Номер карты.
 * @returns {String}
 */
function formatCardNumber(value_) {
    var value = "".concat(value_);
    return cardNumberSplitter(value).reverse().join(' ');
}
/**
 * Склоняет слово в зависимости от количества.
 * Если в строке массива `declensions` присутствует шаблон `%n`, то он будет заменён на `number`.
 *
 * @param {String|Number} number Количество
 * @param {Array.<String>} declensions Варианты окончания - ['месяц', 'месяца', 'месяцев']
 * @returns {String}
 */
function pluralize(number, declensions) {
    var one = declensions[0], two = declensions[1], many = declensions[2];
    var parsedNumber = typeof number === 'string' ? parseInt(number, 10) : number;
    var result;
    var rest = Math.abs(parsedNumber) % 100;
    if (rest > 10 && rest < 20) {
        result = many;
    }
    else {
        rest %= 10;
        if (rest > 1 && rest < 5) {
            result = two;
        }
        else if (rest === 1) {
            result = one;
        }
        else {
            result = many;
        }
    }
    return result.replace(/%n/g, parsedNumber.toString(10));
}
/**
 * Маскирует номер телефона.
 *
 * @param {String} number Номер телефона
 * @returns {String}
 */
function formatMaskedPhone(number) {
    var first = number.substr(0, 2);
    var last = number.substr(number.length - 5, number.length);
    return "".concat(first, " \u00B7\u00B7\u00B7 \u00B7\u00B7\u00B7 ").concat(last);
}
/**
 * Возвращает 4 последних цифры номера карты.
 * Скармливаем 13123123123123686781 и получаем ··6781
 *
 * @param {String} number Номер телефона
 * @returns {String}
 */
function cropAccountNumber(number) {
    if (!number || number.length <= 4) {
        return number;
    }
    return "\u00B7\u00B7".concat(number.substring(number.length - 4, number.length));
}
/**
 * Форматирует номер транзакции.
 *
 * @param {String} [value] Номер транзакции.
 * @param {String} [prefix] Текст, отображаемый перед отформатированным значением.
 * @returns {String} Форматированный номер транзакции.
 */
function formatPaymentNumber(value, prefix) {
    if (!value) {
        return '';
    }
    prefix = prefix ? "".concat(prefix, " ") : '';
    return "".concat(prefix, "\u2116").concat(value);
}
/**
 * Форматирует сумму в соответствии с заданными параметрами.
 *
 * @param {CorporateAmount} term Данные о сумме.
 * @param {Boolean} [params.withCurrency] Отображать unicode символ валюты.
 * @param {Boolean} [params.withMinorUnits] Отображать десятичную часть.
 * @param {Boolean} [params.withMinorZeros] Сохранять нули в десятичной части.
 * @returns {String}
 */
function formatCorporateAmount(term, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.withCurrency, withCurrency = _c === void 0 ? false : _c, _d = _b.withMinorUnits, withMinorUnits = _d === void 0 ? true : _d, _e = _b.withMinorZeros, withMinorZeros = _e === void 0 ? false : _e;
    var _f = term.currency, currency = _f === void 0 ? {} : _f;
    var minorUnits = currency.minorUnits || 1;
    var minorPartLength = withMinorUnits ? Math.log10(minorUnits) : 0;
    var value = (term.amount / minorUnits).toFixed(minorPartLength);
    if (!withMinorZeros) {
        value = value.replace(TRAILING_ZEROS_REGEXP, '');
    }
    var _g = value.split('.'), integerPart = _g[0], decimalPart = _g[1];
    var result = amountSplitter(integerPart).reverse().join(NBSP);
    if (decimalPart) {
        result += ",".concat(decimalPart);
    }
    if (withCurrency && currency.mnemonicCode) {
        result += NBSP + formatCurrency(currency.mnemonicCode);
    }
    return result;
}
/**
 * Форматирует сумму и добавляет символ валюты.
 *
 * @param {CorporateAmount} [term] Данные о сумме.
 * @param {Boolean} [withCurrency] Флаг указывающий о необхомости добавить в вывод символ валюты.
 * @returns {String} Отформатированная сумма.
 */
function formatAmountWithCurrency(term, withCurrency) {
    if (withCurrency === void 0) { withCurrency = true; }
    return "".concat(formatAmount(term.amount / term.currency.minorUnits)).concat(withCurrency ? " ".concat(formatCurrency(term.currency.mnemonicCode)) : '');
}
/**
 * Формирует строку вида `минимум ...` или `максимум ...`
 *
 * @param {CorporateAmount} [min] Сумма минимума.
 * @param {CorporateAmount} [max] Сумма максимума.
 * @returns {String} Отформатированная сумма.
 */
function formatMinMax(min, max) {
    var result = '';
    if (min) {
        result = "\u043C\u0438\u043D\u0438\u043C\u0443\u043C ".concat(formatAmountWithCurrency(min), " ");
    }
    if (max) {
        result += "\u043C\u0430\u043A\u0441\u0438\u043C\u0443\u043C ".concat(formatAmountWithCurrency(max));
    }
    return result;
}
/**
 * Перевод в проценты.
 *
 * @param {CorporatePercents} [percent] Сумма.
 * @returns {String} Строка в процентах.
 */
function formatPercents(percent) {
    return percent ? "".concat(percent.value / percent.minorUnits, " %") : '0 %';
}
/**
 * Форматирование миллисекунд в mm:ss.
 *
 * @param {Number} ms миллисекунды
 * @returns {String} время в формате mm:ss, но если оно более 100 минут,
 * то строка 'более, чем 99:99'
 */
function formatMsAsMinutes(ms) {
    if (ms >= 6000000) {
        return 'более, чем 99:99';
    }
    var totalSeconds = (ms - (ms % 1000)) / 1000;
    var totalMinutes = (totalSeconds - (totalSeconds % 60)) / 60;
    var seconds = totalSeconds % 60;
    var paddedMinutes = "00".concat(totalMinutes).slice(-2);
    var paddedSeconds = "00".concat(seconds).slice(-2);
    return "".concat(paddedMinutes, ":").concat(paddedSeconds);
}
/**
 * Форматирует traceId.
 *
 * @param {String} [id] traceId.
 * @returns {String} Форматированный traceId.
 */
function formatTraceId(id) {
    var result = [];
    id.split('').forEach(function (letter, index) {
        if (index !== 0 && index % 4 === 0) {
            result.push('-');
        }
        result.push(letter);
    });
    return result.join('');
}
/**
 * Форматирует значение из range-slider (c числами).
 *
 * @param {Number} [value] значение из range-slider.
 * @returns {String} форматированное значение из range-slider.
 */
function formatRangeSliderNumbers(value) {
    return value ? String(value).replace(/[^0-9]/g, '') : '';
}
/**
 * Форматирует значение из range-slider (c деньгами).
 *
 * @param {Number} [value] значение из range-slider.
 * @returns {String} форматированное значение из range-slider (999999 -> 999 999).
 */
function formatRangeSliderMoney(value) {
    return value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '';
}
/**
 * Конвертирует сумму из формата для корпоратов в формат для розницы.
 *
 * @typedef {Object} CorporateAmountProps Параметры суммы
 *
 * @param {CorporateAmountProps} amount Параметры суммы
 * @returns {AmountProps}
 */
function convertToCorporateAmount(amount) {
    return {
        value: amount.amount,
        currency: {
            code: amount.currency.mnemonicCode,
            minority: amount.currency.minorUnits,
        },
    };
}
var ACCOUNT_LABELS = {
    MONEY_BOX: 'Налоговая копилка',
    CREDIT_CARD: 'Счёт кредитной карты',
    DEPOSIT: 'Депозит',
    RUBBLE: 'Расчётный счёт',
    CURRENCY: 'Валютный счёт',
    TRANSIT: 'Транзитный счёт',
};
/**
 * Получает читаемый тип счёта
 *
 * @param {CorporateAccount} account Счёт
 * @param {Object} mapping Опциональная подмена названия типа
 * @returns {String}
 */
function getAccountLabel(account, mapping) {
    if (mapping === void 0) { mapping = {}; }
    var currency = account.currency, clientAccountName = account.clientAccountName;
    if (clientAccountName) {
        return clientAccountName;
    }
    var type;
    switch (account.type) {
        case 'Tax':
            type = 'MONEY_BOX';
            break;
        case 'CreditCard':
            type = 'CREDIT_CARD';
            break;
        case 'Deposit':
        case 'DepositIndividual':
            type = 'DEPOSIT';
            break;
        case 'Transit':
            type = 'TRANSIT';
            break;
        default:
            type = currency.mnemonicCode === RUR_CODE ? 'RUBBLE' : 'CURRENCY';
            break;
    }
    return mapping[type] || ACCOUNT_LABELS[type];
}

exports.CURRENCY_MAP = lib_currencyCodes;
exports.ACCOUNT_FORMAT = ACCOUNT_FORMAT;
exports.ACCOUNT_SPLIT_REGEX = ACCOUNT_SPLIT_REGEX;
exports.AMOUNT_DEFORMAT_REGEXP = AMOUNT_DEFORMAT_REGEXP;
exports.AMOUNT_PART_SIZE = AMOUNT_PART_SIZE;
exports.CARD_NUMBER_PART_SIZE = CARD_NUMBER_PART_SIZE;
exports.MDASH = MDASH;
exports.NBH = NBH;
exports.NBSP = NBSP;
exports.convertToCorporateAmount = convertToCorporateAmount;
exports.cropAccountNumber = cropAccountNumber;
exports.deformatAmount = deformatAmount;
exports.formatAccount = formatAccount;
exports.formatAccountWithShrinking = formatAccountWithShrinking;
exports.formatAmount = formatAmount;
exports.formatAmountWithCurrency = formatAmountWithCurrency;
exports.formatCardNumber = formatCardNumber;
exports.formatCorporateAmount = formatCorporateAmount;
exports.formatCurrency = formatCurrency;
exports.formatMaskedPhone = formatMaskedPhone;
exports.formatMinMax = formatMinMax;
exports.formatMsAsMinutes = formatMsAsMinutes;
exports.formatPaymentNumber = formatPaymentNumber;
exports.formatPercents = formatPercents;
exports.formatRangeSliderMoney = formatRangeSliderMoney;
exports.formatRangeSliderNumbers = formatRangeSliderNumbers;
exports.formatTraceId = formatTraceId;
exports.getAccountLabel = getAccountLabel;
exports.pluralize = pluralize;
